import { useEffect, type FC } from 'react';
import { useAtom } from 'jotai';
import { Button, Tag, InlineLoading, InlineNotification } from '@carbon/react';
import { Close } from '@carbon/react/icons';
import moment from 'moment';
import { formatMutationResource } from 'utils/misc';
import { pctScore, actorDisplayName } from './utils';
import { preDrillAgentRunsAtom } from './atoms';
import styles from './agent-runs-panel.module.scss';

interface AgentRunsPanelProps {
  uwi14: string;
  onClose: () => void;
}

const formatRunTime = (iso: string): string => {
  const m = moment(iso);
  return m.isValid() ? m.format('MMM D, YYYY · h:mm A') : iso;
};

// Agent Runs audit (right sidebar): target-zone run history for the subject
// UWI, newest first. POST /queries/pre_drill_agent_runs; read-only.
export const AgentRunsPanel: FC<AgentRunsPanelProps> = ({ uwi14, onClose }) => {
  const [resource, fetchRuns] = useAtom(preDrillAgentRunsAtom);

  useEffect(() => {
    fetchRuns({
      type: 'POST',
      url: '/queries/pre_drill_agent_runs',
      data: {
        query: { uwi14, sort_attribute: 'created_at', sort_direction: 'DESC' },
      },
    });
  }, [uwi14, fetchRuns]);

  const { loading, data, error } = formatMutationResource(resource);
  const runs = data?.results ?? [];

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Agent Runs</h2>
          <p className={styles.subtitle}>Target Zone Identification audit</p>
        </div>
        <Button
          kind="ghost"
          size="sm"
          hasIconOnly
          renderIcon={Close}
          iconDescription="Close"
          onClick={onClose}
        />
      </div>

      <div className={styles.body}>
        {!loading && error?.type && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title="Failed to load agent runs"
          />
        )}
        {loading && !data && (
          <InlineLoading description="Loading agent runs…" />
        )}
        {!loading && !error?.type && runs.length === 0 && (
          <p className={styles.empty}>No agent runs yet.</p>
        )}

        <ol className={styles.runs}>
          {runs.map((run) => (
            <li key={run.id} className={styles.run}>
              <div className={styles.runHead}>
                <span className={styles.runSeq}>{`Run #${run.sequence}`}</span>
                <Tag
                  type={run.actorType === 'manual' ? 'blue' : 'gray'}
                  size="sm"
                >
                  {run.actorType === 'manual' ? 'Manual' : 'Auto'}
                </Tag>
                {run.output.autoConfirmed && (
                  <Tag type="green" size="sm">
                    Auto-confirmed
                  </Tag>
                )}
              </div>
              <p className={styles.runTime}>{formatRunTime(run.createdAt)}</p>
              <p className={styles.runActor}>
                {actorDisplayName(run.actorType, run.actorName)}
              </p>
              {run.reasoning.narrativeSummary && (
                <p className={styles.runSummary}>
                  {run.reasoning.narrativeSummary}
                </p>
              )}
              {run.output.topThree.length > 0 && (
                <dl className={styles.topThree}>
                  {run.output.topThree.map((pick) => (
                    <div key={pick.geologicIntervalId} className={styles.pick}>
                      <span className={styles.pickName}>
                        {`${pick.rank}. ${pick.geologicIntervalName}`}
                      </span>
                      <span className={styles.pickScore}>
                        {pctScore(pick.score)}
                      </span>
                    </div>
                  ))}
                </dl>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};
